import { Popover } from "@base-ui-components/react/popover"
import { Check, Copy, Info } from "lucide-react"
import { Fragment, useEffect, useState } from "react"
import { usePortalContainer } from "../hooks/usePortalContainer"
import { formatDuration, formatTaskCost } from "../persistence/taskStatsUtils"
import type { TaskSessionDebugInfo, TaskStats } from "../store/TaskModel"
import { copyTextToClipboard } from "../utils/clipboard"
import type { WildLarkTaskSessionsListResult } from "../wildlark/client"
import { AppToolsControl } from "./sessions/AppToolsControl"
import { TaskDirectorySelector, type TaskWorktreeControl } from "./TaskGitControl"

export interface TaskSessionInfoDetails {
    taskId: string
    title: string
    workspaceId: string
    /** Running, Closed, Idle, or the current PR automation status. */
    status: string
    createdAt?: string
    usage: TaskStats
    sessions: TaskSessionDebugInfo[]
    context?: WildLarkTaskSessionsListResult | null
    contextLoading?: boolean
    contextError?: string | null
}

interface TaskSessionInfoProps {
    details: TaskSessionInfoDetails
    worktree?: TaskWorktreeControl
    onDirectoryChange?: (path: string | null) => Promise<string | null>
    onOpen?: () => void
    side?: "top" | "bottom" | "left" | "right"
    showDirectorySelector?: boolean
}

export function TaskSessionInfoButton({
    details,
    worktree,
    onDirectoryChange,
    onOpen,
    side = "bottom",
    showDirectorySelector = true,
}: TaskSessionInfoProps): JSX.Element {
    const portalContainer = usePortalContainer()

    return (
        <Popover.Root onOpenChange={(open) => open && onOpen?.()}>
            <Popover.Trigger
                className="btn flex h-7 w-7 flex-shrink-0 items-center justify-center text-muted hover:bg-base-200 hover:text-base-content"
                title="Task and session details"
                aria-label="Task and session details"
            >
                <Info size={14} />
            </Popover.Trigger>
            <Popover.Portal container={portalContainer}>
                <Popover.Positioner side={side} align="end" sideOffset={8} collisionPadding={8}>
                    <Popover.Popup className="z-50 outline-none">
                        <TaskSessionInfoPanel
                            details={details}
                            worktree={worktree}
                            onDirectoryChange={onDirectoryChange}
                            showDirectorySelector={showDirectorySelector}
                        />
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    )
}

export function TaskSessionInfoPanel({ details, worktree, onDirectoryChange, showDirectorySelector = true }: TaskSessionInfoProps): JSX.Element {
    const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle")
    const totalTokens = details.usage.inputTokens + details.usage.outputTokens
    const hasUsage = details.usage.loading || details.usage.costAvailable || details.usage.durationMs > 0 || totalTokens > 0

    useEffect(() => {
        if (copyState !== "copied") return
        const timer = globalThis.setTimeout(() => setCopyState("idle"), 1_500)
        return () => globalThis.clearTimeout(timer)
    }, [copyState])

    const copyDetails = async () => {
        try {
            await copyTextToClipboard(serializeTaskSessionInfo(details, globalThis.location?.href, worktree))
            setCopyState("copied")
        } catch (error) {
            console.error("[TaskSessionInfoButton] Failed to copy task details", error)
            setCopyState("error")
        }
    }

    return (
        <div className="max-h-[min(34rem,calc(100vh-4rem))] w-[min(28rem,calc(100vw-1rem))] overflow-y-auto border border-border bg-base-100 text-xs leading-5 shadow-lg">
            <div className="sticky top-0 flex items-center justify-between gap-4 border-b border-border bg-base-100 px-4 py-3">
                <div className="min-w-0">
                    <div className="text-sm font-semibold leading-5 text-base-content">Task details</div>
                    <div className="text-[11px] leading-4 text-muted">Identifiers and runtime context for this thread</div>
                </div>
                <button
                    type="button"
                    className="btn flex h-7 shrink-0 items-center gap-1.5 border border-border px-2 text-xs text-base-content hover:bg-base-200"
                    onClick={() => void copyDetails()}
                    disabled={details.contextLoading}
                    aria-label="Copy task details"
                >
                    {copyState === "copied" ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                    {details.contextLoading ? "Loading…" : copyState === "copied" ? "Copied" : copyState === "error" ? "Try again" : "Copy"}
                </button>
            </div>

            <dl className="px-4 py-3">
                <DetailRow label="Title" value={details.title || "Untitled"} mono={false} />
                <DetailRow label="Task" value={details.taskId} />
                <DetailRow label="Workspace" value={details.workspaceId} />
                <DetailRow label="Status" value={details.status} mono={false} />
                {details.createdAt && <DetailRow label="Created" value={formatTimestamp(details.createdAt)} mono={false} />}
            </dl>

            {(details.context || details.contextLoading || details.contextError) && (
                <div className="border-t border-border">
                    <div className="px-4 py-2.5 font-semibold text-base-content">Project and task paths</div>
                    <dl className="px-4 pb-3">
                        {details.contextLoading && !details.context && <DetailRow label="Status" value="Loading…" mono={false} />}
                        {details.contextError && <DetailRow label="Context error" value={details.contextError} mono={false} />}
                        {details.context && (
                            <>
                                <DetailRow label="Project root" value={details.context.workspaceRoot} />
                                {details.context.additionalRoots.map((root, index) => (
                                    <DetailRow key={root} label={`Task root ${index + 1}`} value={root} />
                                ))}
                                {details.context.designatedWorktree && <DetailRow label="Designated" value={details.context.designatedWorktree} />}
                                {details.context.selectedWorkingDirectory && <DetailRow label="Selected" value={details.context.selectedWorkingDirectory} />}
                                {details.context.repository && (
                                    <>
                                        <DetailRow label="Repository root" value={details.context.repository.root} />
                                        {details.context.repository.branch && <DetailRow label="Repository branch" value={details.context.repository.branch} />}
                                        {details.context.repository.worktrees.map((tree, index) => (
                                            <Fragment key={tree.worktreeId}>
                                                <DetailRow label={`Worktree ${index + 1}`} value={tree.path} />
                                                {tree.branch && <DetailRow label="Branch" value={tree.branch} />}
                                                {tree.head && <DetailRow label="HEAD" value={tree.head} />}
                                            </Fragment>
                                        ))}
                                    </>
                                )}
                            </>
                        )}
                    </dl>
                </div>
            )}

            {worktree && showDirectorySelector && (
                <div className="border-t border-border">
                    <div className="px-4 py-2.5 font-semibold text-base-content">Working directory</div>
                    <div className="px-4 pb-3">
                        <TaskDirectorySelector worktree={worktree} onDirectoryChange={onDirectoryChange} />
                    </div>
                </div>
            )}

            {hasUsage && (
                <div className="border-t border-border">
                    <div className="px-4 py-2.5 font-semibold text-base-content">Usage</div>
                    <dl className="px-4 pb-3">
                        {details.usage.loading && !details.usage.costAvailable && details.usage.durationMs === 0 && totalTokens === 0 ? (
                            <DetailRow label="Status" value="Loading…" mono={false} />
                        ) : null}
                        {details.usage.costAvailable && <DetailRow label="Cost" value={formatTaskCost(details.usage.totalCostUsd)} mono={false} />}
                        {details.usage.durationMs > 0 && <DetailRow label="Duration" value={formatDuration(details.usage.durationMs)} mono={false} />}
                        {totalTokens > 0 && <DetailRow label="Tokens" value={totalTokens.toLocaleString()} mono={false} />}
                        {details.usage.inputTokens > 0 && <DetailRow label="Input tokens" value={details.usage.inputTokens.toLocaleString()} mono={false} />}
                        {details.usage.outputTokens > 0 && <DetailRow label="Output tokens" value={details.usage.outputTokens.toLocaleString()} mono={false} />}
                    </dl>
                </div>
            )}

            <div className="border-t border-border">
                <div className="flex items-center justify-between px-4 py-2.5 font-semibold text-base-content">
                    <span>Sessions</span>
                    <span className="font-normal tabular-nums text-muted">{details.sessions.length}</span>
                </div>
                {details.sessions.length === 0 ? (
                    <div className="px-4 pb-3 text-muted">No session references yet.</div>
                ) : (
                    details.sessions.map((session, index) => (
                        <Fragment key={session.key}>
                            <SessionDetails session={session} index={index} />
                            {session.nestSessionId && (
                                <AppToolsControl identity={{ taskId: details.taskId, workspaceId: details.workspaceId, sessionId: session.nestSessionId }} />
                            )}
                        </Fragment>
                    ))
                )}
                {details.context && details.context.sessionReads.failed > 0 && (
                    <div className="border-t border-border px-4 py-2 text-muted">
                        Session reads: {details.context.sessionReads.succeeded}/{details.context.sessionReads.requested} succeeded · done with gaps
                    </div>
                )}
            </div>
        </div>
    )
}

function DetailRow({ label, value, mono = true }: { label: string; value: string; mono?: boolean }): JSX.Element {
    return (
        <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-3 py-1">
            <dt className="whitespace-nowrap text-muted">{label}</dt>
            <dd className={`min-w-0 break-all text-base-content ${mono ? "font-mono text-[11px]" : ""}`} title={value}>
                {value}
            </dd>
        </div>
    )
}

function SessionDetails({ session, index }: { session: TaskSessionDebugInfo; index: number }): JSX.Element {
    const label = session.providerId ?? session.harnessId
    return (
        <div className="border-t border-border px-4 py-3">
            <div className="mb-1 flex items-center justify-between gap-3">
                <span className="font-semibold text-base-content">Session {index + 1}</span>
                <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-muted">{label}</span>
                    {session.status && <span className="shrink-0 text-[11px] capitalize leading-4 text-muted">{session.status}</span>}
                </div>
            </div>
            <dl>
                {session.role && <DetailRow label="Role" value={session.role} mono={false} />}
                {session.label && <DetailRow label="Label" value={session.label} mono={false} />}
                {session.parentSessionId && <DetailRow label="Parent" value={session.parentSessionId} />}
                <DetailRow label="Harness" value={session.harnessId} />
                {session.providerId && <DetailRow label="Provider" value={session.providerId} />}
                {session.nestSessionId && <DetailRow label="Nest ID" value={session.nestSessionId} />}
                {session.providerSessionId && <DetailRow label="Provider session" value={session.providerSessionId} />}
                {session.sessionFile && <DetailRow label="Session file" value={session.sessionFile} />}
                {session.sessionRoot && <DetailRow label="Session root" value={session.sessionRoot} />}
                {session.model && <DetailRow label="Model" value={session.model} />}
                {session.mode && <DetailRow label="Mode" value={session.mode} mono={false} />}
                {session.cwd && <DetailRow label="Directory" value={session.cwd} />}
                {session.worktreeId && <DetailRow label="Worktree" value={session.worktreeId} />}
                {session.executionDirectory && <DetailRow label="Future turns" value={session.executionDirectory} />}
                {session.lastUsedAt && <DetailRow label="Last used" value={formatTimestamp(session.lastUsedAt)} mono={false} />}
                {session.error && <DetailRow label="Read error" value={session.error} mono={false} />}
                {session.loadedEventCount !== undefined && (
                    <DetailRow label="Events" value={`${session.loadedEventCount}${session.loadedEventTruncated ? "+" : ""}`} mono={false} />
                )}
            </dl>
        </div>
    )
}

function formatTimestamp(value: string): string {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

export function serializeTaskSessionInfo(details: TaskSessionInfoDetails, url?: string, worktree?: TaskWorktreeControl): string {
    const totalTokens = details.usage.inputTokens + details.usage.outputTokens
    const lines = [
        `WildLark task: ${details.title || "Untitled"}`,
        ...(url ? [`URL: ${url}`] : []),
        `Task ID: ${details.taskId}`,
        `Workspace ID: ${details.workspaceId}`,
        `Status: ${details.status}`,
        ...(details.createdAt ? [`Created: ${details.createdAt}`] : []),
        ...(worktree ? [`Directory: ${worktree.path}`, `Directory selection: ${worktree.selectionMode === "manual" ? "Manual" : "Automatic"}`] : []),
        ...(details.context ? [`Project root: ${details.context.workspaceRoot}`] : []),
        `Sessions: ${details.sessions.length}`,
    ]

    if (details.context) {
        if (details.context.additionalRoots.length > 0) {
            lines.push("Task roots:")
            for (const root of details.context.additionalRoots) lines.push(`  ${root}`)
        }
        if (details.context.designatedWorktree) lines.push(`Designated worktree: ${details.context.designatedWorktree}`)
        if (details.context.selectedWorkingDirectory) lines.push(`Selected working directory: ${details.context.selectedWorkingDirectory}`)
        if (details.context.repository) {
            lines.push(`Repository root: ${details.context.repository.root}`)
            if (details.context.repository.branch) lines.push(`Repository branch: ${details.context.repository.branch}`)
            if (details.context.repository.worktrees.length > 0) {
                lines.push("Worktrees:")
                for (const tree of details.context.repository.worktrees) {
                    lines.push(`  ${tree.path}`)
                    if (tree.branch) lines.push(`    Branch: ${tree.branch}`)
                    if (tree.head) lines.push(`    HEAD: ${tree.head}`)
                }
            }
        }
        if (details.context.sessionReads.failed > 0) {
            lines.push(
                `Session reads: ${details.context.sessionReads.succeeded}/${details.context.sessionReads.requested} succeeded (${details.context.sessionReads.status})`
            )
        }
    }

    if (details.usage.costAvailable || details.usage.durationMs > 0 || totalTokens > 0) {
        lines.push("", "Usage:")
        if (details.usage.costAvailable) lines.push(`  Cost: ${formatTaskCost(details.usage.totalCostUsd)}`)
        if (details.usage.durationMs > 0) lines.push(`  Duration: ${formatDuration(details.usage.durationMs)}`)
        if (totalTokens > 0) lines.push(`  Tokens: ${totalTokens.toLocaleString()}`)
        if (details.usage.inputTokens > 0) lines.push(`  Input tokens: ${details.usage.inputTokens.toLocaleString()}`)
        if (details.usage.outputTokens > 0) lines.push(`  Output tokens: ${details.usage.outputTokens.toLocaleString()}`)
    }

    for (const [index, session] of details.sessions.entries()) {
        lines.push("", `Session ${index + 1}: ${session.providerId ?? session.harnessId}`)
        if (session.role) lines.push(`  Role: ${session.role}`)
        if (session.label) lines.push(`  Label: ${session.label}`)
        if (session.parentSessionId) lines.push(`  Parent session: ${session.parentSessionId}`)
        lines.push(`  Harness: ${session.harnessId}`)
        if (session.providerId) lines.push(`  Provider: ${session.providerId}`)
        if (session.status) lines.push(`  Status: ${session.status}`)
        if (session.nestSessionId) lines.push(`  Nest ID: ${session.nestSessionId}`)
        if (session.providerSessionId) lines.push(`  Provider session ID: ${session.providerSessionId}`)
        if (session.sessionFile) lines.push(`  Session file: ${session.sessionFile}`)
        if (session.sessionRoot) lines.push(`  Session root: ${session.sessionRoot}`)
        if (session.model) lines.push(`  Model: ${session.model}`)
        if (session.mode) lines.push(`  Mode: ${session.mode}`)
        if (session.cwd) lines.push(`  Directory: ${session.cwd}`)
        if (session.worktreeId) lines.push(`  Worktree: ${session.worktreeId}`)
        if (session.executionDirectory) lines.push(`  Future turns: ${session.executionDirectory}`)
        if (session.lastUsedAt) lines.push(`  Last used: ${session.lastUsedAt}`)
        if (session.error) lines.push(`  Read error: ${session.error}`)
        if (session.loadedEventCount !== undefined) {
            lines.push(`  Loaded events: ${session.loadedEventCount}${session.loadedEventTruncated ? "+" : ""}`)
        }
    }

    return lines.join("\n")
}
